<template>
    <div>
        <div
            v-if="recurringInformationOptions.length > 1"
            class="flex flex-col flex-wrap gap-1"
        >
            <div
                v-for="option in recurringInformationOptions"
                :key="option.key"
                class="flex items-center"
            >
                <radio-button
                    :model-value="recurringInformationChoice.key"
                    :value="option.key"
                    :input-id="option.key"
                    name="recurring-information"
                    :disabled="isProcessing"
                    @update:model-value="recurringInformationChoice = option"
                />
                <label
                    :for="option.key"
                    class="ml-2"
                >
                    {{ option.text }}
                </label>
            </div>
        </div>

        <div
            v-if="
                error &&
                recurringInformationChoice.source.data.__typename !==
                    'PaymentSourceDirectDebitCardpointe'
            "
            class="text-red-600 mt-2 text-sm font-medium"
        >
            {{ error || '&nbsp;' }}
        </div>

        <hosted-payment-form
            v-if="
                recurringInformationChoice.source.data.__typename ===
                'PaymentSourceCreditCardCardpointe'
            "
            :payment-source="recurringInformationChoice.source.data"
            :is-processing="isProcessing"
            @payment-method="handleRecurringPaymentMethod"
        />

        <stripe-form
            v-if="
                recurringInformationChoice.source.data.__typename ===
                'PaymentSourceCreditCardStripe'
            "
            ref="stripeFormRef"
            :checkout-id="checkoutId"
            :payment-source-id="recurringInformationChoice.source.id"
            :is-processing="isProcessing"
            class="mt-2"
            @payment-method="handleRecurringPaymentMethod"
        />

        <windcave-form
            v-if="
                recurringInformationChoice.source.data.__typename ===
                'PaymentSourceCreditCardWindcave'
            "
            ref="windcaveFormRef"
            :payment-source-id="recurringInformationChoice.source.id"
            :is-processing="isProcessing"
            :input="paymentMethodInput"
            class="mt-2"
            @payment-method="handleRecurringPaymentMethod"
        />

        <fake-payment-source-form
            v-if="
                recurringInformationChoice.source.data.__typename ===
                'PaymentSourceCustom'
            "
            ref="fakePaymentSourceFormRef"
            :display-name="recurringInformationChoice.source.data.displayName"
            :warning-text="recurringInformationChoice.source.data.warningText"
            @payment-method="handleRecurringPaymentMethod"
        />

        <add-bank-account-form
            v-if="
                recurringInformationChoice.source.data.__typename ===
                'PaymentSourceDirectDebitCardpointe'
            "
            ref="bankAccountFormRef"
            class="mt-4"
            @payment-method="handleRecurringPaymentMethod"
        />
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const NewRecurringMethodPaymentSourceFragment = graphql(`
    fragment NewRecurringMethodPaymentSourceFragment on PaymentSource {
        id
        data {
            __typename
            ... on PaymentSourceCreditCardCardpointe {
                ...HostedPaymentFormPaymentSourceFragment
            }
            ... on PaymentSourceCustom {
                displayName
                warningText
            }
        }
    }
`)
</script>

<script setup lang="ts">
import AddBankAccountForm from '~/components/Checkout/Sections/Payment/AddBankAccountForm.vue'
import HostedPaymentForm from '~/components/Payments/HostedPaymentForm.vue'
import StripeForm from '~/components/Payments/StripeForm.vue'
import RadioButton from 'primevue/radiobutton'
import { computed, ref, watch } from 'vue'
import { useShowAlert } from '~/composables/use-show-alert'
import { type FragmentType, getFragmentData } from '~/resources/graphql'
import { useI18n } from 'vue-i18n'
import {
    type BankAccountPaymentMethod,
    NewBankAccountKey,
    type PaymentMethodProperties,
    type TokenPaymentMethod
} from '~/components/Checkout/Sections/Payment/payment-method'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import WindcaveForm from '~/components/Payments/WindcaveForm.vue'
import type { PaymentMethodInput } from '~/components/Checkout/Sections/use-payment-method-input'
import FakePaymentSourceForm from '~/components/Payments/FakePaymentSourceForm.vue'

const props = defineProps<{
    paymentSources: FragmentType<
        typeof NewRecurringMethodPaymentSourceFragment
    >[]
    paymentMethodInput: PaymentMethodInput
    error?: string
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: PaymentMethodProperties | null): void
}>()

const { t } = useI18n({ useScope: 'global' })

const { checkoutId, isProcessing } = useCheckoutFormProcessing()

const paymentSourcesData = computed(() => {
    return props.paymentSources.map(paymentSource => {
        return getFragmentData(
            NewRecurringMethodPaymentSourceFragment,
            paymentSource
        )
    })
})

const showAlert = useShowAlert()
const recurringInformationOptions = computed(() => {
    const choices = paymentSourcesData.value.map(source => {
        if (source.data === null) {
            throw new Error()
        }

        return {
            key: source.id,
            text:
                source.data.__typename === 'PaymentSourceDirectDebitCardpointe'
                    ? t('checkoutSections.bankAccount')
                    : source.data.__typename === 'PaymentSourceCustom'
                      ? source.data.displayName
                      : t('checkoutSections.creditDebitCard'),
            source
        }
    })

    if (choices.length === 0) {
        showAlert(
            t('headsUp'),
            t('checkoutSections.invalidCheckoutConfiguration')
        )
    }

    return choices
})

const recurringInformationChoice = ref(recurringInformationOptions.value[0])
watch(
    recurringInformationChoice,
    newValue => {
        emit('paymentMethod', null)
    },
    { immediate: true }
)

const stripeFormRef = ref<InstanceType<typeof StripeForm>>()
const bankAccountFormRef = ref<InstanceType<typeof AddBankAccountForm>>()
const windcaveFormRef = ref<InstanceType<typeof WindcaveForm>>()
const fakePaymentSourceFormRef =
    ref<InstanceType<typeof FakePaymentSourceForm>>()
async function submitRecurringForm() {
    if (stripeFormRef.value !== undefined && stripeFormRef.value !== null) {
        await stripeFormRef.value.submitStripeForm()
    }

    if (windcaveFormRef.value !== undefined && windcaveFormRef.value !== null) {
        await windcaveFormRef.value.submitPaymentMethod()
    }

    if (
        bankAccountFormRef.value !== undefined &&
        bankAccountFormRef.value !== null
    ) {
        await bankAccountFormRef.value.submitBankAccountForm()
    }

    if (
        fakePaymentSourceFormRef.value !== undefined &&
        fakePaymentSourceFormRef.value !== null
    ) {
        fakePaymentSourceFormRef.value.submitFakePaymentSourceForm()
    }
}

defineExpose({
    submitRecurringForm
})

function handleRecurringPaymentMethod(
    paymentMethod:
        | TokenPaymentMethod
        | BankAccountPaymentMethod
        | null
        | undefined
) {
    if (paymentMethod === null || paymentMethod === undefined) {
        emit('paymentMethod', null)
        return
    }

    if (paymentMethod.type === 'BankAccountPaymentMethod') {
        emit('paymentMethod', {
            key: NewBankAccountKey,
            paymentSourceId: recurringInformationChoice.value.source.id,
            displayInfo: {
                isCard: false,
                brand: t('checkoutSections.bankAccount'),
                lastFour: paymentMethod.accountNumber.substring(
                    paymentMethod.accountNumber.length - 4
                ),
                accountExpirationDate: null,
                lastUsedDate: null
            },
            paymentTodayAllowed: false,
            data: {
                type: 'CardpointeDirectDebit',
                routingNumber: paymentMethod.routingNumber,
                accountNumber: paymentMethod.accountNumber,
                accountNumberConfirm: paymentMethod.accountNumberConfirm,
                accountType: paymentMethod.accountType
            }
        })
        return
    }

    emit('paymentMethod', {
        key: paymentMethod.token,
        paymentSourceId: recurringInformationChoice.value.source.id,
        displayInfo: {
            isCard: paymentMethod.isCard,
            brand: paymentMethod.brand,
            lastFour: paymentMethod.lastFour,
            accountExpirationDate: paymentMethod.accountExpirationDate,
            lastUsedDate: null
        },
        paymentTodayAllowed: paymentMethod.paymentTodayAllowed,
        data:
            recurringInformationChoice.value.source.data?.__typename ===
            'PaymentSourceCreditCardStripe'
                ? {
                      type: 'Stripe',
                      token: paymentMethod.token,
                      amount: '0'
                  }
                : recurringInformationChoice.value.source.data?.__typename ===
                    'PaymentSourceCreditCardWindcave'
                  ? {
                        type: 'WindcaveCreditCard',
                        token: paymentMethod.token
                    }
                  : recurringInformationChoice.value.source.data?.__typename ===
                      'PaymentSourceCustom'
                    ? {
                          type: 'CustomPaymentSource',
                          token: paymentMethod.token
                      }
                    : {
                          type: 'CardpointeCreditCard',
                          token: paymentMethod.token
                      }
    })
}
</script>
